// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useState, useRef, useEffect } from 'react';
import './Tooltip.css';

export interface TooltipProps {
  content: React.ReactNode;
  trigger: React.ReactElement;
  position?: 'top' | 'bottom' | 'left' | 'right';
  size?: 'mini' | 'small';
  className?: string;
}

export function Tooltip({
  content,
  trigger,
  position = 'top',
  size,
  className = '',
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // A pending show-timer must not fire setState after unmount.
  useEffect(() => clearTimer, []);

  const handleMouseEnter = () => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      setVisible(true);
    }, 200);
  };

  const handleMouseLeave = () => {
    clearTimer();
    setVisible(false);
  };

  const tooltipClasses = [
    'fin-tooltip',
    `fin-tooltip--${position}`,
    size && `fin-tooltip--${size}`,
    visible ? 'fin-tooltip--visible' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const wrapperClasses = ['fin-tooltip-wrapper', className].filter(Boolean).join(' ');

  return (
    <span
      className={wrapperClasses}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {trigger}
      <span className={tooltipClasses} role="tooltip">
        {content}
      </span>
    </span>
  );
}
